import { SyntheticEvent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from '@navi/web-ui/lib/primitives';
import { RadioGroup } from '@navi/web-ui/lib/components';
import { RootState } from '@cp/src/store';
import PrefilledTag from '../PreAudit/PrefilledTag';
import {
  AuditQuestionConfig,
  CallingAgentAuditDetails,
  ApiAuditTrailAnswer
} from '../../interface';
import { SingleSelectQuestionsProps } from '../../interface';
import { getRadioOptions } from '../../utils';
import { AuditorType } from '../auditEnums';

const SingleSelectQuestions = (props: SingleSelectQuestionsProps) => {
  const { questions, getSelectedOption, handleSingleSelectChange, isFormDisabled, readOnly } =
    props;

  const auditDetails = useSelector(
    (state: RootState) => state.callAudit?.callingAgentAuditDetails
  ) as CallingAgentAuditDetails | null;

  const auditQuestionnaire = (auditDetails?.auditConfig?.auditQuestionnaire ||
    []) as AuditQuestionConfig[];
  const auditTrail = auditDetails?.auditTrail || [];

  const dsAuditTrail = useMemo(
    () => auditTrail.find(t => t.auditor === AuditorType.DS),
    [auditTrail]
  );
  const qcAgentAuditTrail = useMemo(
    () => auditTrail.find(t => t.auditor === AuditorType.QC_AGENT),
    [auditTrail]
  );
  const qcTeamLeadAuditTrail = useMemo(
    () => auditTrail.find(t => t.auditor === AuditorType.QC_TEAM_LEAD),
    [auditTrail]
  );

  const dsAnswers = (dsAuditTrail?.answers || []) as unknown as ApiAuditTrailAnswer[];
  const qcAgentAnswers = (qcAgentAuditTrail?.answers || []) as unknown as ApiAuditTrailAnswer[];

  const getAnswerValue = (
    questionId: number,
    answersSource: ApiAuditTrailAnswer[]
  ): string | null => {
    const answer = answersSource.find((a: ApiAuditTrailAnswer) => a.question_id === questionId);
    if (!answer) return null;
    const question = auditQuestionnaire.find(q => q.question_id === questionId);
    if (!question) return null;
    const selectedOption = question.options.find(opt =>
      answer.selected_option_ids.includes(opt.option_id)
    );
    return selectedOption?.option_text || null;
  };

  const getPreAuditValue = (questionId: number): string | null =>
    getAnswerValue(questionId, dsAnswers);

  const getQcAgentValue = (questionId: number): string | null =>
    getAnswerValue(questionId, qcAgentAnswers);

  const shouldShowDsPreAuditTag = (questionId: number) =>
    !readOnly &&
    dsAuditTrail &&
    (qcAgentAuditTrail || qcTeamLeadAuditTrail) &&
    getPreAuditValue(questionId);

  const shouldShowQcAgentTag = (questionId: number) =>
    !readOnly && qcAgentAuditTrail && qcTeamLeadAuditTrail && getQcAgentValue(questionId);

  return (
    <div className="flex flex-col gap-2 p-3">
      {questions.map(question => (
        <div key={question.question_id} className="w-full">
          <Typography variant="h4" color="var(--grayscale-2)">
            {question.question_text}
          </Typography>
          <RadioGroup
            name={`question-${question.question_id}`}
            value={getSelectedOption(question.question_id)}
            onChange={(event: SyntheticEvent) => {
              const value = (event?.target as HTMLInputElement)?.value;
              handleSingleSelectChange(question.question_id, parseInt(value, 10));
            }}
            options={getRadioOptions(question.options)}
            disabled={isFormDisabled}
          />
          <div className="flex flex-row gap-2 flex-wrap">
            {shouldShowDsPreAuditTag(question.question_id) && (
              <PrefilledTag
                preAuditDoneBy={dsAuditTrail?.auditor || ''}
                preAuditText={getPreAuditValue(question.question_id) || ''}
              />
            )}
            {shouldShowQcAgentTag(question.question_id) && (
              <PrefilledTag
                preAuditDoneBy={qcAgentAuditTrail?.auditor || ''}
                preAuditText={getQcAgentValue(question.question_id) || ''}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SingleSelectQuestions;
